import { useMemo, useState } from 'react';

import { Modal } from '@thefarmersfront/kitchen';

import { ModalContentsContainer, ModalLabelValueRow, ModalSection, Table } from '~/components/common';

import { useModal, useStableCallback } from '~/hooks/common';
import {
  flattenExchangeInspections,
  getClaimExchangeDetailDeliveryColumnDefs,
  getClaimExchangeDetailInspectionColumnDefs,
  getClaimExchangeDetailTrackingColumnDefs,
} from '~/utils/claim';

import { ClaimDetailInspectionImageModal } from '../../ClaimDetailInspectionImageModal';

import type { TypeExchangeDetailData } from '~/types/claim.types';

import styles from './ClaimExchangeDetailModal.module.scss';

const ClaimExchangeDetailModal = ({
  data,
  isOpen,
  onConfirm,
  onClickOutboundOrderCode,
}: {
  data: TypeExchangeDetailData | null;
  isOpen: boolean;
  onConfirm: () => void;
  onClickOutboundOrderCode: (outboundOrderCode: string) => void;
}) => {
  const [selectedImageList, setSelectedImageList] = useState<string[] | null>(null);

  const inspectionImageModal = useModal();
  const { handleOpenModal: openInspectionImageModal } = inspectionImageModal;

  const { columnDefs: trackingColumnDefs } = useMemo(() => getClaimExchangeDetailTrackingColumnDefs(), []);
  const handleClickViewInspectionImage = useStableCallback((imageList: string[]) => {
    setSelectedImageList(imageList);
    openInspectionImageModal();
  });
  const handleClickOutboundOrderCode = useStableCallback(onClickOutboundOrderCode);
  const { columnDefs: inspectionColumnDefs } = useMemo(
    () => getClaimExchangeDetailInspectionColumnDefs({ onClickViewInspectionImage: handleClickViewInspectionImage }),
    [handleClickViewInspectionImage]
  );
  const { columnDefs: deliveryInfoColumnDefs } = useMemo(
    () => getClaimExchangeDetailDeliveryColumnDefs({ onClickOutboundOrderCode: handleClickOutboundOrderCode }),
    [handleClickOutboundOrderCode]
  );
  const inspectionRows = useMemo(
    () => flattenExchangeInspections(data?.returnOrder.returnGoodsInspections ?? []),
    [data?.returnOrder.returnGoodsInspections]
  );

  if (!data) return null;

  return (
    <>
      <Modal title="교환 신청 상세" size="xlarge" open={isOpen} onConfirm={onConfirm}>
        <ModalContentsContainer>
          <ModalSection title="회수 신청 정보">
            <div className={styles['info-container']}>
              <ModalLabelValueRow label="교환요청번호" value={data.exchangeReceiptId} />
              <ModalLabelValueRow label="원주문 수령자명" value={data.returnOrder.member.name} />
              <ModalLabelValueRow label="기본주소" value={data.returnOrder.returnPlace.primaryAddress} />
              <ModalLabelValueRow label="상세주소" value={data.returnOrder.returnPlace.secondaryAddress ?? '-'} />
            </div>
          </ModalSection>
          <ModalSection title="회수 송장 정보">
            <Table
              className={styles['table']}
              data={data.returnOrder.returnOrderTrackings ?? []}
              columns={trackingColumnDefs}
              rowStatusType="NO_DATA"
            />
          </ModalSection>
          <ModalSection title="검수 처리 상세">
            <Table
              className={styles['table']}
              data={inspectionRows}
              columns={inspectionColumnDefs}
              getRowId={(row) => row.rowId}
              rowStatusType="NO_DATA"
            />
          </ModalSection>
          <ModalSection title="교환 배송 정보">
            <Table
              className={styles['table']}
              data={data.exchangeOrder.exchangeOrderItems ?? []}
              columns={deliveryInfoColumnDefs}
              rowStatusType="NO_DATA"
            />
          </ModalSection>
        </ModalContentsContainer>
      </Modal>
      {inspectionImageModal.isOpen && selectedImageList && (
        <ClaimDetailInspectionImageModal
          mode="exchange"
          isOpen={inspectionImageModal.isOpen}
          imageList={selectedImageList}
          onClose={inspectionImageModal.handleCloseModal}
        />
      )}
    </>
  );
};

export default ClaimExchangeDetailModal;
